/**
 * Logger controlado para producao
 *
 * Em producao (NODE_ENV === 'production'), apenas erros criticos sao logados.
 * Em desenvolvimento, todos os logs sao exibidos.
 *
 * Uso:
 *   import { logger } from '@/lib/logger';
 *   logger.debug('Mensagem de debug');
 *   logger.info('Informacao');
 *   logger.warn('Aviso');
 *   logger.error('Erro', error);
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  minLevel: LogLevel;
  prefix: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Configuracao baseada no ambiente
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

const config: LoggerConfig = {
  enabled: !isProduction, // Desabilita logs em producao por padrao
  minLevel: isProduction ? 'error' : 'debug', // Em producao, so erros
  prefix: '[CLM]',
};

function shouldLog(level: LogLevel): boolean {
  if (!config.enabled && level !== 'error') {
    return false;
  }
  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

function formatMessage(level: LogLevel, message: string): string {
  if (isDevelopment) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    return `${config.prefix} [${timestamp}] [${level.toUpperCase()}] ${message}`;
  }
  return `${config.prefix} ${message}`;
}

export const logger = {
  /**
   * Log de debug - apenas em desenvolvimento
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (shouldLog('debug')) {
      console.log(formatMessage('debug', message), ...args);
    }
  },

  /**
   * Log informativo - apenas em desenvolvimento
   */
  info: (message: string, ...args: unknown[]): void => {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message), ...args);
    }
  },

  /**
   * Log de aviso - apenas em desenvolvimento
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', message), ...args);
    }
  },

  /**
   * Log de erro - sempre exibido (mesmo em producao)
   * Use apenas para erros que precisam ser investigados
   */
  error: (message: string, error?: unknown): void => {
    if (shouldLog('error')) {
      if (error instanceof Error) {
        console.error(formatMessage('error', message), {
          name: error.name,
          message: error.message,
          stack: isDevelopment ? error.stack : undefined,
        });
      } else if (error !== undefined) {
        console.error(formatMessage('error', message), error);
      } else {
        console.error(formatMessage('error', message));
      }
    }
  },

  /**
   * Habilita/desabilita logs em runtime
   */
  setEnabled: (enabled: boolean): void => {
    config.enabled = enabled;
  },

  /**
   * Define o nivel minimo de log
   */
  setMinLevel: (level: LogLevel): void => {
    config.minLevel = level;
  },

  /**
   * Retorna se esta em modo de desenvolvimento
   */
  isDev: (): boolean => isDevelopment,

  /**
   * Retorna se esta em modo de producao
   */
  isProd: (): boolean => isProduction,
};

// Desabilita logs do Supabase em producao
if (isProduction && typeof window !== 'undefined') {
  // Silencia logs internos do Supabase
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__SUPABASE_DEBUG__ = false;
}

export default logger;
